/**
 * receipt-rows.rules.ts
 *
 * Pure assembly of the `receipts[]` structured ledger rows sent on
 * `POST /V1/receiving-log` (§9), from the Items step's working rows
 * (`CheckableLineItem`). Shared by standard-flow lines, legacy-page-two lines,
 * and off-PO items so `ReviewStepComponent.buildRequest` doesn't have to know
 * the receiving-quantity rule itself. Kept out of the component so it's
 * unit-testable independent of submission plumbing.
 *
 * Receipt-quantity rule: `quantityReceived` on a receipt is the amount
 * received in THIS event, not a cumulative total. A countable Complete tap sends
 * the outstanding delta (`quantityExpected − quantityReceived` already on the
 * ledger) so it doesn't double-count prior receipts; a countable Partial input
 * is already a this-event amount and is sent as typed. A ragged line has no
 * count: both its Partial and its Complete send `quantityReceived: null` — one
 * receipt PER listed part (part in `itemDescription`) — and Complete is a pure
 * "line done" close (`completesLine: true`) that requires no part entry at all.
 * Missing is derived and note-only — untouched lines with no human close and
 * manual free-text contribute no receipt row.
 */

import { CheckableLineItem, LineDamage, lineKey } from '../../../service/receiving-mobile/log-entry-draft.store';
import { ReceiptSubmitRow } from '../../../models/logs/ReceiptSubmitRow';
import { isRaggedLine, splitRaggedParts } from './line-receipts.rules';

/**
 * Receipt rows plus, parallel to them, the source line's key for each row. The
 * key lets the caller map a damaged line's photos to that line's index in the
 * `receipts[]` array (the `damage-{receiptIndex}-{n}` filename convention),
 * since one line can produce several receipt rows (ragged parts).
 */
export interface BuiltReceiptRows {
  rows: ReceiptSubmitRow[];
  /** `sourceLineKeys[i]` is the `lineKey` of the line that produced `rows[i]`. */
  sourceLineKeys: string[];
}

/**
 * Builds the `receipts[]` payload from the Items step's working rows.
 *
 * @param lineItemRows - PO line items (plus any off-PO rows) with their in-progress receiving status.
 * @param damageByLine - per-line damage reports, keyed by `lineKey`; damaged lines get `isDamaged`/`damageNote`/`damageDisposition` stamped on every receipt row they produce, and refused/returned rows have `quantityReceived` forced to 0.
 * @returns The receipt rows to submit; a ragged partial contributes one row per part, every other
 *          line at most one. Canceled, missing, and untouched-and-still-open lines contribute none.
 */
export function buildReceiptRows(
  lineItemRows: CheckableLineItem[],
  damageByLine: Record<string, LineDamage> = {},
): ReceiptSubmitRow[] {
  return buildReceiptRowsWithSource(lineItemRows, damageByLine).rows;
}

/**
 * Same assembly as `buildReceiptRows`, but also returns the source line key for
 * each row so damage photos can be renamed to the receipt index they belong to.
 */
export function buildReceiptRowsWithSource(
  lineItemRows: CheckableLineItem[],
  damageByLine: Record<string, LineDamage> = {},
): BuiltReceiptRows {
  const rows: ReceiptSubmitRow[] = [];
  const sourceLineKeys: string[] = [];

  for (const row of lineItemRows) {
    if (row.isCanceled) { continue; }
    const key = lineKey(row);
    const damage = damageByLine[key];
    for (const receipt of buildLineReceipts(row)) {
      rows.push(damage ? applyDamage(receipt, damage) : receipt);
      sourceLineKeys.push(key);
    }
  }

  return { rows, sourceLineKeys };
}

/**
 * Stamps a line's damage onto one of its receipt rows: the flag, the note, the
 * damaged quantity, and the disposition. Damage is a QUANTITY, so only the damaged
 * units are affected. A `Refuse`/`Return` disposition means those units were NOT
 * accepted: they're subtracted from the row's `quantityReceived` (the good units
 * still count) and `completesLine` is cleared (the refused units are outstanding —
 * they fold into `missingItems`; see `buildMissingItems`). The row is still sent so
 * its damage photos have a receipt to attach to. `Keep` leaves the received
 * quantity and close flag as computed (damaged units count as received).
 *
 * `damagedQuantity` is capped at what the row received; on a ragged/no-count row
 * (`quantityReceived == null`) there's no quantity, so the disposition applies to
 * the whole row (`damagedQuantity` null, `completesLine` cleared if not kept).
 */
function applyDamage(receipt: ReceiptSubmitRow, damage: LineDamage): ReceiptSubmitRow {
  const notReceived = damage.disposition === 'Refuse' || damage.disposition === 'Return';
  const arrived = receipt.quantityReceived;
  const damagedQuantity = arrived != null
    ? Math.min(Math.max(Math.floor(Number(damage.quantity)) || 0, 0), arrived)
    : null;
  const refused = notReceived && damagedQuantity != null ? damagedQuantity : 0;
  return {
    ...receipt,
    isDamaged: true,
    damageNote: damage.note.trim() || null,
    damagedQuantity,
    damageDisposition: damage.disposition,
    quantityReceived: arrived != null ? Math.max(arrived - refused, 0) : arrived,
    completesLine: notReceived ? false : receipt.completesLine,
  };
}

function buildLineReceipts(row: CheckableLineItem): ReceiptSubmitRow[] {
  if (row.isOffPo) { return [buildOffPoRow(row)]; }

  switch (row.status) {
    case 'complete':
      return buildCompleteRows(row);
    case 'partial':
      return buildPartialRows(row);
    default:
      // Untouched on this screen — still missing unless the missing page
      // human-closed it (outstanding qty edited to zero, or "line is done").
      // Missing itself is note-only (§7) and writes no receipt row; a human
      // close carries no quantity/status of its own since nothing physically
      // arrived in this event.
      return row.completesLine ? [buildHumanCloseRow(row)] : [];
  }
}

function buildOffPoRow(row: CheckableLineItem): ReceiptSubmitRow {
  const qty = row.quantityReceived ?? (row.quantity ? Number(row.quantity) : null);
  return {
    poLineSequence: null,
    itemNumber: row.itemNumber ?? '',
    itemDescription: row.description,
    quantityReceived: qty != null && !isNaN(qty) ? qty : null,
    unitOfMeasure: row.unit || '',
    receiptStatus: 'Complete',
    completesLine: false,
    notes: null,
  };
}

/**
 * A Complete tap. A countable line sends the outstanding delta (see
 * `buildCompleteRow`). A ragged line has no inferable count — completing it is a
 * pure "line done" close, so it sends `completesLine: true` with
 * `quantityReceived: null` (never a fabricated count, which would inflate the
 * aggregate the backend keeps at 0 for ragged lines). The user is not required
 * to list anything; if they optionally did, each part becomes its own receipt
 * with the last one closing the line. A line counts as ragged when it already
 * has ragged part receipts or the user entered parts text this delivery.
 */
function buildCompleteRows(row: CheckableLineItem): ReceiptSubmitRow[] {
  const parts = splitRaggedParts(row.partsReceived);
  const ragged = isRaggedLine(row.receipts) || parts.length > 0;

  if (!ragged) { return [buildCompleteRow(row)]; }

  if (parts.length > 0) {
    // Optional final parts listed — one receipt each, the last closing the line.
    return parts.map((part, idx) => ({
      poLineSequence: row.poLineSequence ?? null,
      itemNumber: row.itemNumber ?? '',
      itemDescription: part,
      quantityReceived: null,
      unitOfMeasure: row.unit || '',
      receiptStatus: 'Complete' as const,
      completesLine: idx === parts.length - 1,
      notes: null,
    }));
  }

  // Nothing listed — a single "line done" close, no count.
  return [{
    poLineSequence: row.poLineSequence ?? null,
    itemNumber: row.itemNumber ?? '',
    itemDescription: row.description,
    quantityReceived: null,
    unitOfMeasure: row.unit || '',
    receiptStatus: 'Complete',
    completesLine: true,
    notes: null,
  }];
}

/** A countable Complete line sends the outstanding delta (not the full expected qty) so it doesn't double-count. */
function buildCompleteRow(row: CheckableLineItem): ReceiptSubmitRow {
  const quantityReceived = completeReceiptQty(row);
  return {
    poLineSequence: row.poLineSequence ?? null,
    itemNumber: row.itemNumber ?? '',
    itemDescription: row.description,
    quantityReceived,
    unitOfMeasure: row.unit || '',
    receiptStatus: 'Complete',
    // Always true regardless of the delta (including the 0 case, §9).
    completesLine: true,
    notes: null,
  };
}

function completeReceiptQty(row: CheckableLineItem): number | null {
  if (row.quantityExpected != null && row.quantityReceived != null) {
    return Math.max(row.quantityExpected - row.quantityReceived, 0);
  }
  const qty = Number(row.quantity);
  return isNaN(qty) ? null : qty;
}

/**
 * A Partial line is either a ragged parts-text line (§4) or a countable qty
 * line. Ragged → one receipt per part (`quantityReceived: null`, the part in
 * `itemDescription`), `completesLine` set on the finishing (last) part only.
 * Countable → a single receipt carrying the this-event quantity.
 */
function buildPartialRows(row: CheckableLineItem): ReceiptSubmitRow[] {
  const parts = splitRaggedParts(row.partsReceived);

  if (parts.length > 0) {
    return parts.map((part, idx) => ({
      poLineSequence: row.poLineSequence ?? null,
      itemNumber: row.itemNumber ?? '',
      itemDescription: part,
      quantityReceived: null,
      unitOfMeasure: row.unit || '',
      receiptStatus: 'Partial' as const,
      // Only the finishing part closes the line (and only if the line is done).
      completesLine: !!row.completesLine && idx === parts.length - 1,
      notes: null,
    }));
  }

  const receivedQty = Number(row.receivedQty);
  return [{
    poLineSequence: row.poLineSequence ?? null,
    itemNumber: row.itemNumber ?? '',
    itemDescription: row.description,
    quantityReceived: isNaN(receivedQty) ? null : receivedQty,
    unitOfMeasure: row.unit || '',
    receiptStatus: 'Partial',
    completesLine: !!row.completesLine,
    notes: null,
  }];
}

/**
 * A legacy page-two human close on a line untouched this event (§3, §9):
 * nothing physically arrived, so there's no quantity or arrival status —
 * only the "this line is done" declaration.
 */
function buildHumanCloseRow(row: CheckableLineItem): ReceiptSubmitRow {
  return {
    poLineSequence: row.poLineSequence ?? null,
    itemNumber: row.itemNumber ?? '',
    itemDescription: row.description,
    quantityReceived: null,
    unitOfMeasure: row.unit || '',
    receiptStatus: null,
    completesLine: true,
    notes: null,
  };
}
